import { useState, useEffect } from "react";
import ManagerVersionApi from "../../services/settings";
import NetworkApi from "../../services/networks";
import Message from "../../fragments/form/Message";
import Error from "../../fragments/form/Error";
import DashboardHeader from "../../components/DashboardHeader"

const NETWORK_SETTINGS_ID = 1;

const AppSettings = (props) => {
    const [settings, setSettings] = useState({
        id: "",
        ios_current_version: "",
        android_current_version: "",
        ios_min_version: "",
        android_min_version: "",
    });
    const [networkMissionStatement, setNetworkMissionStatement] = useState("");
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [networkLoading, setNetworkLoading] = useState(false);
    const [message, setMessage] = useState({
        type: 'success',
        content: ''
    })
    const managerVersionApi = new ManagerVersionApi(props.token);
    const networkApi = new NetworkApi(props.token);

    const getManagerVersion = async () => {
        try {
            let data = await managerVersionApi.get()
            if (!data?.manager_version) {
                setIsEditMode(false);
            } else {
                setIsEditMode(true);
                setSettings({
                    id: data.manager_version.id,
                    ios_current_version: data.manager_version.ios_current_version,
                    android_current_version: data.manager_version.android_current_version,
                    ios_min_version: data.manager_version.ios_min_version,
                    android_min_version: data.manager_version.android_min_version,
                })
            }
        } catch (error) {
            console.error("Failed to get settings.", error);
            setMessage({
                type: 'error',
                content: error?.message || "Failed to load settings."
            });
        }
    }

    const getNetworkSettings = async () => {
        try {
            const network = await networkApi.getById(NETWORK_SETTINGS_ID);
            setNetworkMissionStatement(network?.mission_statement ?? "");
        } catch (error) {
            console.error("Failed to get network settings.", error);
            setMessage({
                type: 'error',
                content: error?.message || "Failed to load network settings."
            });
        }
    };

    useEffect(() => {
        getManagerVersion();
        getNetworkSettings();
    }, []);

    const handleCreate = async () => {
        setLoading(true);
        try {
            let { id, ...rest } = settings;
            await managerVersionApi.create(rest)
            setMessage({
                type: 'success',
                content: "Settings saved successfully!"
            });
            await getManagerVersion()
        } catch (error) {
            console.error("Failed to save settings.");
            setMessage({
                type: 'error',
                content: error.message || "Failed to save settings."
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        setLoading(true);
        try {
            let { id, ...rest } = settings;
            await managerVersionApi.update(settings.id, rest)
            setMessage({
                type: 'success',
                content: "Settings saved successfully!"
            });
            await getManagerVersion();
        } catch (error) {
            console.error("Failed to save settings.");
            setMessage({
                type: 'error',
                content: error.message || "Failed to save settings."
            });
        } finally {
            setLoading(false);
        }
    };

    const handleNetworkMissionStatementSave = async () => {
        setNetworkLoading(true);
        setMessage({ type: 'success', content: '' });
        try {
            await networkApi.update(NETWORK_SETTINGS_ID, { mission_statement: networkMissionStatement });
            setMessage({
                type: 'success',
                content: "Network mission statement saved successfully!"
            });
        } catch (error) {
            console.error("Failed to save network settings.", error);
            const status = error?.response?.status;
            const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
            const content = status === 403
                ? (apiMessage || "Editing this network is not allowed.")
                : (apiMessage || error?.message || "Failed to save network mission statement.");
            setMessage({
                type: 'error',
                content
            });
        } finally {
            setNetworkLoading(false);
        }
    };

    return (
        <div className='dashboard-content'>
            <div className="d-flex justify-content-end"> <DashboardHeader btnText='' />  </div>
            <div className='container p-3'>
                {
                    message.content && (message.type === 'success' ? <Message message={message.content} /> : <Error message={message.content} />)
                }
                <div className="card mt-2 settings-card-container">
                    <h2 className="mx-1 mb-3 settings-title">App Settings</h2>
                    <div className="mb-3">
                        <label className="form-label">Current Version (iOS)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={settings.ios_current_version}
                            onChange={(e) => setSettings({ ...settings, ios_current_version: e.target.value })}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Current Version (Android)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={settings.android_current_version}
                            onChange={(e) => setSettings({ ...settings, android_current_version: e.target.value })}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Min Version (iOS)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={settings.ios_min_version}
                            onChange={(e) => setSettings({ ...settings, ios_min_version: e.target.value })}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Min Version (Android)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={settings.android_min_version}
                            onChange={(e) => setSettings({ ...settings, android_min_version: e.target.value })}
                        />
                    </div>
                    <div className="d-flex justify-content-end">
                        <button className="btn btn-primary" onClick={isEditMode ? handleUpdate : handleCreate} disabled={loading}>
                            {loading ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
                <div className="card mt-2 settings-card-container">
                    <h2 className="mx-1 mb-3 settings-title">Network Settings (Network {NETWORK_SETTINGS_ID})</h2>
                    <div className="mb-3">
                        <label className="form-label">Mission Statement</label>
                        <textarea
                            className="form-control"
                            rows={4}
                            value={networkMissionStatement}
                            onChange={(e) => setNetworkMissionStatement(e.target.value)}
                            placeholder="Enter the network mission statement..."
                        />
                    </div>
                    <div className="d-flex justify-content-end">
                        <button className="btn btn-primary" onClick={handleNetworkMissionStatementSave} disabled={networkLoading}>
                            {networkLoading ? "Saving..." : "Save Mission Statement"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppSettings;